import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { App, ConfigProvider, Modal, Segmented } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import Plans from '@/pages/payment/Plans';
import Recharge from '@/pages/payment/Recharge';
import Checkout from '@/pages/payment/Checkout';
import { createStyles } from 'antd-style';
import { formatLocaleMessage as t, getAntdLocale } from '@/utils/i18n';

type TabKey = 'plans' | 'recharge';
type ViewKey = 'select' | 'checkout';

type ShowOptions = {
  defaultTab?: TabKey;
};

let root: Root | null = null;
let container: HTMLDivElement | null = null;
let currentVisible = false;
let currentOptions: ShowOptions = {};

const useStyles = createStyles(({ token, css }) => ({
  modal: css`
    .ant-modal-content {
      border-radius: 24px;
      overflow: visible;
      padding: 18px 18px 18px;
      background: linear-gradient(180deg,
        color-mix(in srgb, ${token.colorBgContainer} 98%, ${token.colorFillQuaternary}) 0%,
        ${token.colorBgContainer} 100%
      );
      box-shadow: 0 30px 80px -42px rgba(15, 23, 42, 0.35);
    }
    .ant-modal-header {
      margin-bottom: 0;
      padding: 4px 8px 0;
      background: transparent;
      border-top-left-radius: 20px;
      border-top-right-radius: 20px;
    }
  `,
  modalWrap: css`
    position: relative;
  `,
  headerRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-width: 0;
  `,
  headerTitle: css`
    min-width: 0;
    font-size: 32px;
    line-height: 1.1;
    font-weight: 900;
    color: ${token.colorTextHeading};
    letter-spacing: -0.03em;
  `,
  headerActions: css`
    display: inline-flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  `,
  topTabs: css`
    padding: 4px;
    border-radius: 14px;
    background: color-mix(in srgb, ${token.colorFillQuaternary} 78%, ${token.colorBgContainer});
    border: 1px solid color-mix(in srgb, ${token.colorBorderSecondary} 78%, ${token.colorBgContainer});
    box-shadow: inset 0 1px 0 color-mix(in srgb, white 68%, transparent);

    .ant-segmented-group {
      gap: 2px;
    }

    .ant-segmented-item {
      border-radius: 10px;
      color: ${token.colorTextSecondary};
      transition: color 0.2s ease, background-color 0.2s ease;
    }

    .ant-segmented-item-label {
      min-width: 54px;
      padding: 8px 14px;
      font-size: 14px;
      line-height: 20px;
      font-weight: 700;
    }

    .ant-segmented-item-selected {
      background: ${token.colorBgContainer};
      color: ${token.colorTextHeading};
      box-shadow:
        0 6px 16px -14px rgba(15, 23, 42, 0.24),
        inset 0 1px 0 color-mix(in srgb, white 75%, transparent);
    }
  `,
  checkoutBack: css`
    appearance: none;
    border: none;
    background: transparent;
    padding: 0;
    color: ${token.colorTextSecondary};
    font-size: 14px;
    line-height: 22px;
    font-weight: 700;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
      color: ${token.colorPrimary};
    }
  `,
  bodyScroll: css`
    max-height: min(820px, calc(84vh - 90px));
    overflow: auto;
    padding: 18px 6px 4px;

    @media (max-width: 768px) {
      max-height: min(820px, calc(84vh - 78px));
      padding: 14px 2px 2px;
    }
  `,
  closeBtn: css`
    position: absolute;
    top: 4px;
    right: 4px;
    transform: translate(50%, -50%);
    width: 42px;
    height: 42px;
    border-radius: 999px;
    background: ${token.colorBgElevated};
    border: 1px solid ${token.colorBorderSecondary};
    box-shadow: 0 18px 34px -20px rgba(0, 0, 0, 0.55);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 3;
    transition: transform 160ms ease, border-color 160ms ease;
  `,
  closeBtnHover: css`
    &:hover {
      transform: translate(50%, -50%) scale(1.02);
      border-color: ${token.colorPrimaryBorder};
    }
  `,
}));

function getContainer() {
  if (!container) {
    container = document.createElement('div');
    container.id = 'global-payment-modal';
    document.body.appendChild(container);
  }
  return container;
}

function PaymentModalContent(props: { open: boolean; onClose: () => void; options: ShowOptions }) {
  const { open, onClose, options } = props;
  const { styles } = useStyles();
  const [tab, setTab] = React.useState<TabKey>(options.defaultTab || 'plans');
  const [view, setView] = React.useState<ViewKey>('select');
  const [checkoutCtx, setCheckoutCtx] = React.useState<any>(null);
  const modalWidth =
    view === 'checkout'
      ? 'min(920px, calc(100vw - 24px))'
      : tab === 'recharge'
        ? 'min(980px, calc(100vw - 32px))'
        : 'min(1280px, calc(100vw - 36px))';

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setTab(options.defaultTab || 'plans');
    setView('select');
    setCheckoutCtx(null);
  }, [open, options.defaultTab]);

  const startCheckout = (ctx: any) => {
    setCheckoutCtx(ctx);
    setView('checkout');
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      // 使用 `destroyOnHidden` 对齐 antd 新版生命周期，避免旧属性弃用告警。
      destroyOnHidden
      width={modalWidth}
      className={styles.modal}
      closable={false}
      title={
        view === 'select' ? (
          <div className={styles.headerRow}>
            <div className={styles.headerTitle}>{t('components.paymentModal.title', 'Subscription & Recharge')}</div>
            <Segmented
              size="small"
              className={styles.topTabs}
              value={tab}
              onChange={(v) => setTab(v as TabKey)}
              options={[
                { label: t('components.paymentModal.tab.plans', 'Plans'), value: 'plans', disabled: false },
                { label: t('components.paymentModal.tab.recharge', 'Recharge'), value: 'recharge', disabled: false },
              ]}
            />
          </div>
        ) : (
          <div className={styles.headerRow}>
            <div className={styles.headerTitle}>{t('components.paymentModal.checkoutTitle', 'Confirm Payment')}</div>
            <button
              type="button"
              onClick={() => {
                setView('select');
                setCheckoutCtx(null);
              }}
              className={styles.checkoutBack}
            >
              {t('components.paymentModal.back', 'Back')}
            </button>
          </div>
        )
      }
      styles={{
        body: {
          padding: 0,
          overflow: 'hidden',
        },
      }}
      modalRender={(node) => (
        <div className={styles.modalWrap}>
          {node}
          <button
            type="button"
            aria-label={t('components.paymentModal.close', 'Close')}
            className={`${styles.closeBtn} ${styles.closeBtnHover}`}
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
        </div>
      )}
    >
      <div className={styles.bodyScroll}>
        {view === 'select' ? (
          tab === 'plans' ? (
            <Plans embedded onStartCheckout={startCheckout} />
          ) : (
            <Recharge embedded onStartCheckout={startCheckout} />
          )
        ) : (
          <Checkout embedded ctx={checkoutCtx} onDone={onClose} />
        )}
      </div>
    </Modal>
  );
}

function render(visible: boolean, options: ShowOptions = {}) {
  currentVisible = visible;
  currentOptions = options;

  const containerEl = getContainer();
  if (!root) {
    root = createRoot(containerEl);
  }

  const onClose = () => render(false, currentOptions);

  root.render(
    <ConfigProvider locale={getAntdLocale()}>
      <App>
        <PaymentModalContent open={visible} onClose={onClose} options={options} />
      </App>
    </ConfigProvider>,
  );
}

export const paymentModal = {
  show(options?: ShowOptions) {
    render(true, options || {});
  },
  hide() {
    render(false, currentOptions);
  },
  isVisible() {
    return currentVisible;
  },
};
